import { useEffect, useState, useRef } from "react";
import { Message } from "primereact/message";
import { camelCase } from "lodash";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { loadLevelsAsync, loadLevelDataAsync } from "@/api/assignments";
import type { Level, DirectoryUserShape, StoredUser, FormValues } from "@/api/assignments";
import type { AvailableUsersMap } from "@/hooks/useAvailableUsers";
import type { MutableRefObject } from "react";
import AudienceUsersList from "@/components/AudienceUsersList";

interface AssignmentAudienceProps {
  values: FormValues["target"];
  handleChange: (values: FormValues["target"]) => void;
  showValidationMessages?: boolean;
  validationErrors?: { hasError?: boolean; message?: string };
  onSelectedUsersChange?: (users: DirectoryUserShape[]) => void;
  onAddUsersAndContinue?: MutableRefObject<(() => void) | null>;
  selectedUsersToAdd: DirectoryUserShape[];
  setSelectedUsersToAdd: (users: DirectoryUserShape[]) => void;
  selectedUsersToRemove: DirectoryUserShape[];
  setSelectedUsersToRemove: (users: DirectoryUserShape[]) => void;
  selectedLevel?: string[];
  onLevelChange?: (levels: string[]) => void;
  showNewHire?: boolean;
  newHireValue?: boolean;
  onNewHireChange?: (v: boolean) => void;
  showProcessingMessage?: boolean;
}

const levelsWithoutSkipped = (levels: Level[]): Level[] =>
  levels.filter(l => l.rankName !== "awareness");

const AssignmentAudience = ({
  values,
  handleChange,
  onSelectedUsersChange,
  onAddUsersAndContinue,
  selectedUsersToAdd,
  setSelectedUsersToAdd,
  selectedUsersToRemove,
  setSelectedUsersToRemove,
  selectedLevel,
  onLevelChange,
  showNewHire = true,
  newHireValue = false,
  onNewHireChange,
  showProcessingMessage = false,
}: AssignmentAudienceProps) => {
  const [levelOptions, setLevelOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [levelUserData, setLevelUserData] = useState<Record<string, string[]>>({});
  const abortRef = useRef<AbortController | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    const loadData = async () => {
      try {
        const [levels, levelData] = await Promise.all([loadLevelsAsync(), loadLevelDataAsync()]);

        if (controller.signal.aborted) return;

        const filtered = levelsWithoutSkipped(levels);
        const options: Array<{ label: string; value: string }> = [];
        if (filtered[0]) {
          options.push({ label: "No Completed Levels", value: "whiteBelt" });
        }
        filtered.forEach(level => {
          if (!level.id) return;
          const nextLevel = filtered.find(l => l.id === level.id! + 1);
          if (nextLevel?.name) {
            options.push({
              label: `Completed ${level.name ?? level.rankName ?? ""}`,
              value: camelCase(nextLevel.name),
            });
          }
        });

        setLevelOptions(options);
        setLevelUserData(levelData);
      } catch (err) {
        if (controller.signal.aborted) return;
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to load level data. Please refresh and try again.",
        });
        console.error("Failed to load level data:", err);
      }
    };

    loadData();

    return () => {
      controller.abort();
    };
  }, []);

  // Filter available users by selected levels client-side.
  // Returns undefined (not []) when data isn't loaded or no UUIDs match so
  // AudienceUsersList falls back to showing all available users.
  const filteredAvailableUsers: DirectoryUserShape[] | undefined = (() => {
    if (!selectedLevel || selectedLevel.length === 0) return undefined;
    if (Object.keys(levelUserData).length === 0) return undefined;

    const allowedUuids = new Set<string>();
    selectedLevel.forEach(levelValue => {
      const uuids = levelUserData[levelValue] ?? [];
      uuids.forEach(uuid => allowedUuids.add(uuid));
    });

    if (allowedUuids.size === 0) return undefined;

    return (values.availableUsers ?? []).filter(
      u => u.directoryUuid && allowedUuids.has(u.directoryUuid)
    );
  })();

  return (
    <div>
      {showProcessingMessage && (
        <Message
          severity="info"
          text="We are still processing your recent user changes. Results may not reflect the latest updates."
          className="mb-4 w-full"
        />
      )}

      <AudienceUsersList
        values={{
          ...values,
          users: values.users ?? [],
          availableUsers: values.availableUsers ?? [],
          filteredAvailableUsers,
          allUsers: values.allUsers as Map<string, AvailableUsersMap> | undefined,
        }}
        handleChange={
          handleChange as (v: {
            users: StoredUser[];
            availableUsers: DirectoryUserShape[];
            filteredAvailableUsers?: DirectoryUserShape[];
            allUsers?: Map<string, AvailableUsersMap>;
          }) => void
        }
        newHireValue={newHireValue}
        handleNewHireChange={onNewHireChange}
        showNewHire={showNewHire}
        onSelectedUsersChange={onSelectedUsersChange}
        onAddUsersAndContinue={onAddUsersAndContinue}
        selectedUsersToAdd={selectedUsersToAdd}
        setSelectedUsersToAdd={setSelectedUsersToAdd}
        selectedUsersToRemove={selectedUsersToRemove}
        setSelectedUsersToRemove={setSelectedUsersToRemove}
        levelOptions={levelOptions}
        selectedLevel={selectedLevel}
        onLevelChange={onLevelChange}
        entityType="Assignment"
      />
    </div>
  );
};

export default AssignmentAudience;
